/**
 * How long ago, in the one form this app says it — and kept true while it is on
 * screen.
 *
 * There were two of these — the explorations pane's rows and the palette's
 * Recent block — and they had already drifted apart: one switched to days at 24
 * hours and the other at 48, so the same document read “1d ago” in one list and
 * “30h ago” in the other. The Library then wrote a third ("3 hours ago", and the
 * UTC day after a day). Two surfaces listing the SAME index may not disagree
 * about when it was last touched, so the words live in `core/dates.ts` with every
 * other date, and this is the React half.
 *
 * A row that printed "6m ago" went on printing it for as long as nothing else
 * re-rendered the pane: an exploration left open over lunch still said its twin
 * was saved six minutes ago. `useAgo` wakes once, when the text next changes
 * (`agoChangesIn`), rather than polling — and never once the age is a date.
 */

import { useEffect, useState } from 'react'
import { ago, agoChangesIn } from '../../core/dates'

export { ago }

/** `ago(t)`, re-rendered when it next reads differently. Null prints nothing and
 *  sets no timer, so a row that shows no age can still call the hook. */
export function useAgo(t: number | null): string {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (t === null) return
    const wait = agoChangesIn(t)
    if (wait === null) return
    const id = setTimeout(() => setTick((n) => n + 1), wait)
    return () => clearTimeout(id)
  }, [t, tick])
  return t === null ? '' : ago(t)
}

/** `useAgo` as an element, for a list whose rows are not components of their own. */
export function Ago({ at }: { at: number }) {
  return useAgo(at)
}
